import {Component, computed, DestroyRef, ElementRef, inject, input, OnInit, output, signal, viewChild} from "@angular/core";
import {EventEmitterService, GlobalEventEmitterService} from "@event-emitter";
import {MenuNavItem, SimActiveRecordingsService, SimMenuApptileService, SimUserExtendedService} from "@services";
import {Translations} from "@translations";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {NonNullableFormBuilder, ReactiveFormsModule} from "@angular/forms";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {Observable} from "rxjs";
import {map, startWith} from "rxjs/operators";
import {cloneDeep} from "lodash-es";
import {SimUser} from "@models";
import {MatFormField, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {RouterLink, RouterLinkActive} from "@angular/router";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatRipple} from "@angular/material/core";
import {MatInput} from "@angular/material/input";
import {StopPropagationDirective} from "@common/pipes/stop-propagation.directive";

@Component({
	selector: "sim-side-navigation-menu",
	templateUrl: "./side-navigation-menu.component.html",
	styleUrls: ["./side-navigation-menu.component.scss"],
	providers: [EventEmitterService],
	imports: [
		MatFormField,
		TranslatePipe,
		MatIcon,
		MatLabel,
		MatSuffix,
		RouterLinkActive,
		MatRipple,
		MatIconButton,
		MatInput,
		RouterLink,
		ReactiveFormsModule,
		StopPropagationDirective,
		MatButton,
	],
})
export class SideNavigationMenuComponent implements OnInit {
	// Constants
	T = Translations;

	// I/O
	public readonly navigationMenuOpened = input.required<Observable<void>>();
	public readonly closeSidenavEmitter = output<void>();

	// Properties
	protected readonly navigationFilterCtrl = inject(NonNullableFormBuilder).control("");
	protected activeTrainingSessionIdForRecorder: string | null = null;

	// Injections
	public readonly appTileService = inject(SimMenuApptileService);
	readonly #activeRecordingsService = inject(SimActiveRecordingsService);
	readonly #eventEmitter = inject(GlobalEventEmitterService);
	readonly #userService = inject(SimUserExtendedService);
	readonly translate = inject(TranslateService);
	readonly #destroyRef = inject(DestroyRef);

	// Derived Properties
	protected readonly menuItems = signal<MenuNavItem[]>(this.appTileService.MenuItems);
	protected readonly navigationFilter = toSignal<string>(this.navigationFilterCtrl.valueChanges.pipe(startWith("")), {
		requireSync: true,
	});
	protected readonly user = toSignal<SimUser>(this.#userService.userChangedEmitter.pipe(map((source) => source.user)));
	protected readonly isFiltered = computed(() => !!this.navigationFilter().trim());
	protected readonly filteredMenuItems = computed<MenuNavItem[]>(() => {
		//menu permissions have to be rechecked when user changes (e.g. login/logout)
		const _ = this.user();
		return this.computeFilteredMenuItems(this.menuItems(), this.navigationFilter());
	});

	// Children
	protected readonly searchInput = viewChild.required<ElementRef<HTMLInputElement>>("sidenavSearchFieldInput");

	ngOnInit(): void {
		this.#activeRecordingsService.activeTrainingSession.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((data) => {
			this.activeTrainingSessionIdForRecorder = data.trainingSessionId;
		});

		this.#eventEmitter.closeSideNavigation.subscribe(({visible}) => {
			if (visible) {
				this.closeSideNav();
			}
		});

		this.navigationMenuOpened().subscribe(() => {
			this.navigationFilterCtrl.reset();
			setTimeout(() => this.searchInput().nativeElement.focus(), 100);
		});
	}

	protected closeSideNav(): void {
		setTimeout(() => this.closeSidenavEmitter.emit(), 500);
	}

	protected toggleOpenCategory(categoryDiv: HTMLDivElement) {
		categoryDiv.classList.toggle("open");
	}

	private computeFilteredMenuItems(menuItems: MenuNavItem[], navigationFilterSignal: string): MenuNavItem[] {
		const filterSearch = (items: MenuNavItem[] | undefined, filterString: string) => {
			if (!filterString) {
				return items as MenuNavItem[];
			}
			return (items ?? [])
				.map((item) => {
					const translatedName = this.translate.instant(item.name).toLowerCase();
					// If the parent matches the filter, include all its children without filtering
					// otherwise, filter its children recursively
					if (!translatedName.includes(filterString)) {
						item.children = filterSearch(item.children, filterString);
						// Exclude if no children match the filter
						if (!item.children?.length) {
							return null;
						}
					}
					return item;
				})
				.filter((item) => !!item);
		};

		const filterPermissions = (items: MenuNavItem[] | undefined) => {
			return (items ?? [])
				.map((item) => {
					// Include only if the item has all prerequisites
					if (!item.hasAllPrerequisites(this.#userService)) {
						return null;
					}

					// check children permissions recursively
					item.children = filterPermissions(item.children);
					return item;
				})
				.filter((item) => !!item);
		};

		const filterEmptyNodes = (items: MenuNavItem[] | undefined) => {
			return (items ?? [])
				.map((item) => {
					item.children = filterEmptyNodes(item.children);

					// Only include the item if it has children or a routeLink
					if (item.children?.length > 0 || !!item.routeLink) {
						return item;
					}
				})
				.filter((item) => !!item);
		};

		menuItems = cloneDeep(menuItems);
		menuItems = filterPermissions(menuItems);
		menuItems = filterSearch(menuItems, navigationFilterSignal?.toLowerCase());
		menuItems = filterEmptyNodes(menuItems);
		return menuItems;
	}
}
